<template>
  <div v-if="show" class="hm-backdrop" @click.self="$emit('close')">
    <div class="hm-modal" role="dialog" aria-modal="true">
      <div class="hm-head">
        <h3>{{ title }}</h3>
        <button class="hm-close" aria-label="Close" @click="$emit('close')">×</button>
      </div>
      <div class="hm-body">
        <p v-if="intro" class="oh-intro">{{ intro }}</p>
        <div v-if="!items.length" class="oh-empty">{{ emptyText }}</div>

        <!-- Page title history (title + H1 with SEO char counts) -->
        <div v-else-if="variant === 'title'" class="th-list">
          <div
            v-for="h in items"
            :key="h.versionId"
            class="th-entry"
            :class="{ current: h.versionId === currentVersionId }"
          >
            <div class="th-top">
              <span class="th-src" :class="titleSrc(h.source).cls">{{ titleSrc(h.source).label }}</span>
              <span class="th-time">{{ formatDate(h.createdAt) }}</span>
              <span class="th-actions">
                <span v-if="h.versionId === currentVersionId" class="th-current-tag">Current</span>
                <button v-else class="hm-btn" @click="$emit('restore', h.versionId)">Load</button>
              </span>
            </div>
            <div class="th-fields">
              <div class="th-field">
                <span class="th-tag">Title</span>
                <span class="th-val">{{ h.seoTitle }}</span>
                <span class="th-count" :class="{ over: (h.seoTitle || '').length > 60 }">{{ (h.seoTitle || '').length }}/60</span>
              </div>
              <div class="th-field">
                <span class="th-tag">H1</span>
                <span class="th-val">{{ h.pageH1 }}</span>
                <span class="th-count" :class="{ over: (h.pageH1 || '').length > 70 }">{{ (h.pageH1 || '').length }}/70</span>
              </div>
            </div>
            <div v-if="h.optLabel || h.cost" class="th-meta">
              <span v-if="h.optLabel">From <b>{{ h.optLabel }}</b></span>
              <span v-if="h.cost">Cost <b>{{ h.cost }}</b></span>
            </div>
          </div>
        </div>

        <div v-else class="oh-list">
          <div
            v-for="h in items"
            :key="h.versionId"
            class="oh-entry"
            :class="{ current: h.versionId === currentVersionId }"
          >
            <div class="oh-main">
              <div class="oh-top">
                <span class="oh-badge" :class="badge(h.source).cls">{{ badge(h.source).label }}</span>
                <span class="oh-time">{{ formatDate(h.createdAt) }}</span>
                <span v-if="h.versionId === currentVersionId" class="oh-current-tag">Current</span>
              </div>
              <div v-if="h.headings != null" class="oh-summary">
                <strong>{{ h.sections }}</strong> section{{ h.sections === 1 ? '' : 's' }} ·
                <strong>{{ h.headings }}</strong> heading{{ h.headings === 1 ? '' : 's' }} ·
                ~<strong>{{ (h.words || 0).toLocaleString() }}</strong> words
              </div>
              <div v-if="h.instruction" class="oh-note">{{ h.instruction }}</div>
              <div v-if="h.headingPreview && h.headingPreview.length" class="oh-preview">
                <div v-for="(r, j) in h.headingPreview" :key="j" class="oh-line" :class="r.kind">
                  <span class="oh-tag">{{ (r.kind || 'h2').toUpperCase() }}</span>
                  <span>{{ r.text }}</span>
                </div>
                <div v-if="h.moreHeadings" class="oh-more">
                  + {{ h.moreHeadings }} more heading{{ h.moreHeadings === 1 ? '' : 's' }}
                </div>
              </div>
            </div>
            <button
              v-if="h.versionId !== currentVersionId"
              class="hm-btn oh-restore"
              @click="$emit('restore', h.versionId)"
            >Restore</button>
          </div>
        </div>
      </div>
      <div class="hm-foot">
        <button class="hm-btn" @click="$emit('close')">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
// Shared version-history modal (outline / section / title), matching the
// "Subheadings history" design handoff. Items only need {versionId, source,
// createdAt}; the richer summary/preview blocks render when the API supplies
// them (today only the outline history does).
const BADGES = {
  generate: { label: "Generate", cls: "generate" },
  regenerate: { label: "Regenerate", cls: "regenerate" },
  edit: { label: "Edit", cls: "edit" },
  leave_as_is: { label: "Left as is", cls: "edit" },
  fix: { label: "Fix", cls: "edit" },
  polish: { label: "Polish", cls: "edit" },
};

// Source pill for the "title" variant (matches the Page-title-history design):
// generate → default, refine/regenerate → purple, manual edit → blue.
const TITLE_SRC = {
  generate: { label: "Generate", cls: "" },
  regenerate: { label: "Regenerate", cls: "refine" },
  refine: { label: "Refine", cls: "refine" },
  edit: { label: "Manual edit", cls: "manual" },
};

export default {
  name: "AiwV2HistoryModal",
  props: {
    show: { type: Boolean, default: false },
    title: { type: String, required: true },
    intro: { type: String, default: "" },
    items: { type: Array, default: () => [] },
    emptyText: { type: String, default: "No history yet." },
    // "outline" (default, oh-* entries) | "title" (th-* entries: title + H1)
    variant: { type: String, default: "outline" },
  },
  computed: {
    // The version matching the live content (backend sends `current`); fall back
    // to the newest item when the backend doesn't flag one (e.g. Title history).
    currentVersionId() {
      const cur = this.items.find((h) => h.current);
      return cur ? cur.versionId : (this.items.length ? this.items[0].versionId : null);
    },
  },
  methods: {
    badge(source) {
      return BADGES[source] || { label: source || "Version", cls: "edit" };
    },
    titleSrc(source) {
      return TITLE_SRC[source] || { label: source || "Version", cls: "" };
    },
    formatDate(s) {
      if (!s) return "";
      return String(s).replace("T", " ").slice(0, 16);
    },
  },
};
</script>

<style scoped>
/* Modal shell — the design's .modal-backdrop / .modal.wide / .modal-head|body|foot,
   renamed with an `hm-` prefix: Bulma is loaded globally and its `.modal` sets
   `display: none` + `position: fixed` + @extend %overlay, which silently hid the
   box (only the backdrop showed, since Bulma has no .modal-backdrop). */
.hm-backdrop {
  position: fixed; inset: 0;
  background: rgba(15,20,25,.45);
  display: flex;
  align-items: center; justify-content: center;
  z-index: 200;
  padding: 20px;
  animation: hmFadeIn .15s ease;
}
@keyframes hmFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
.hm-modal {
  background: var(--surface, #fff);
  border-radius: var(--r-xl, 14px);
  max-width: 760px;
  width: 100%;
  box-shadow: var(--shadow-lg, 0 10px 36px rgba(15,20,25,.08));
  max-height: 90vh;
  overflow: auto;
  font-family: "Mulish", system-ui, sans-serif;
}
.hm-head { padding: 20px 24px 8px; display: flex; align-items: flex-start; gap: 12px; }
.hm-head h3 { margin: 0; font-size: 17px; flex: 1; color: var(--text, #052e33); }
.hm-close { background: none; border: none; color: var(--text-3, #6b7b7e); cursor: pointer; font-size: 22px; padding: 0; line-height: 1; }
.hm-body { padding: 4px 24px 8px; font-size: 13.5px; color: var(--text-2, #014751); }
.hm-body p { margin: 6px 0; }
.hm-foot { padding: 14px 24px 20px; display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid var(--border, #e4e4e4); margin-top: 12px; }

/* Buttons — the design's .btn.btn-soft.btn-sm, folded into one prefixed class. */
.hm-btn {
  font-family: inherit; cursor: pointer;
  padding: 6px 12px; border-radius: 7px;
  font-weight: 600; font-size: 12px;
  display: inline-flex; align-items: center; gap: 6px;
  background: #fff; color: var(--text, #052e33);
  border: 1px solid var(--border-strong, #ccc);
  transition: all .15s;
}
.hm-btn:hover { background: var(--surface-2, #f7f7f7); }

/* Outline history entries */
.oh-intro { color: var(--text-3, #6b7b7e); margin-bottom: 10px !important; }
.oh-empty { color: var(--text-3, #6b7b7e); font-size: 12.5px; padding: 6px 0 10px; }
.oh-list { display: flex; flex-direction: column; gap: 10px; max-height: 58vh; overflow: auto; padding: 2px 2px 4px; }
.oh-entry {
  border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px);
  background: var(--surface, #fff); padding: 13px 15px;
  display: flex; align-items: flex-start; gap: 14px;
}
/* #F4FBF8 == color-mix(in srgb, var(--pop-green) 5%, #fff) — precomputed for support. */
.oh-entry.current { border-color: var(--pop-green, #25a97a); background: #f4fbf8; }
.oh-main { flex: 1; min-width: 0; }
.oh-top { display: flex; align-items: center; gap: 8px; margin-bottom: 3px; }
.oh-badge { font-size: 11px; font-weight: 700; letter-spacing: .02em; padding: 2px 8px; border-radius: 999px; text-transform: capitalize; }
.oh-badge.generate { background: rgba(37,169,122,.16); color: #2b6b3a; }
.oh-badge.regenerate { background: rgba(200,83,31,.14); color: #c8531f; }
.oh-badge.edit { background: var(--surface-2, #f7f7f7); color: var(--text-2, #014751); }
.oh-time { font-size: 12.5px; color: var(--text-3, #6b7b7e); }
.oh-current-tag { font-size: 11px; font-weight: 700; color: var(--pop-green, #25a97a); margin-left: auto; }
.oh-summary { font-size: 12.5px; color: var(--text-2, #014751); margin: 2px 0 8px; }
/* The design's var(--text-1) is undefined and silently inherits; `inherit` also
   dodges Bulma's global `strong` colour override. */
.oh-summary strong { color: inherit; font-weight: 600; }
.oh-note { font-size: 12px; color: var(--text-3, #6b7b7e); font-style: italic; margin-bottom: 8px; }
.oh-preview { display: flex; flex-direction: column; gap: 3px; }
.oh-line { font-size: 12.5px; color: var(--text-2, #014751); line-height: 1.35; display: flex; gap: 7px; }
.oh-line.h3 { padding-left: 15px; color: var(--text-3, #6b7b7e); }
.oh-tag {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 10px; font-weight: 700;
  color: var(--text-3, #6b7b7e); background: var(--surface-2, #f7f7f7);
  border-radius: 4px; padding: 0 5px; height: 16px; line-height: 16px; flex: none;
}
.oh-more { font-size: 12px; color: var(--text-3, #6b7b7e); margin-top: 2px; }
.oh-restore { flex: none; align-self: center; }

/* Page title history entries (design handoff — SEO title + H1 with char counts) */
.th-list { display: flex; flex-direction: column; gap: 10px; max-height: 58vh; overflow: auto; padding: 2px 2px 4px; }
.th-entry { border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 13px 15px 12px; background: #fff; }
.th-entry.current { border-color: var(--pop-green, #25a97a); background: #f4fbf8; }
.th-top { display: flex; align-items: center; gap: 8px; margin-bottom: 9px; }
.th-src { font-size: 10px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; padding: 3px 8px; border-radius: 100px; background: var(--surface-2, #f7f7f7); color: var(--pop-ink, #012323); }
.th-src.refine { background: var(--pop-purple-soft, #e3eeef); color: var(--pop-purple, #014751); }
.th-src.manual { background: var(--pop-blue-soft, #c7ddff); color: var(--pop-blue, #316bff); }
.th-time { font-size: 12px; color: var(--text-3, #6b7b7e); font-variant-numeric: tabular-nums; }
.th-actions { margin-left: auto; display: inline-flex; align-items: center; gap: 8px; }
.th-current-tag { font-size: 11px; font-weight: 700; color: var(--pop-green, #25a97a); }
.th-fields { display: grid; gap: 7px; }
.th-field { display: grid; grid-template-columns: 50px 1fr auto; gap: 10px; align-items: baseline; }
.th-tag { font-size: 10.5px; font-weight: 800; letter-spacing: .04em; color: var(--text-3, #6b7b7e); text-transform: uppercase; }
.th-val { font-size: 13.5px; line-height: 1.45; color: var(--text, #052e33); font-weight: 600; }
.th-count { font-size: 11px; color: var(--text-3, #6b7b7e); font-variant-numeric: tabular-nums; white-space: nowrap; }
.th-count.over { color: var(--pop-orange-deep, #b23d00); font-weight: 700; }
.th-meta { margin-top: 9px; padding-top: 9px; border-top: 1px dashed var(--border, #e4e4e4); display: flex; flex-wrap: wrap; gap: 14px; font-size: 11.5px; color: var(--text-3, #6b7b7e); }
.th-meta b { color: var(--text-2, #014751); font-weight: 700; }
</style>
